/**
 * Fetches model IDs from Google Generative Language API that support generateContent.
 * Names match exactly what Google returns (no guessed "-latest" suffixes).
 */

export type GeminiListModelsResult =
  | { ok: true; modelIds: string[] }
  | { ok: false; error: string };

/**
 * Keep generative chat models; drop embeddings, QA-only, and other non-chat entries that may appear in ListModels.
 */
export function filterGeminiModelsForChat(modelIds: string[]): string[] {
  return modelIds.filter((id) => {
    const lower = id.toLowerCase();
    if (lower.includes('embedding')) return false;
    if (lower.includes('textembedding')) return false;
    if (lower.includes('embed-')) return false;
    if (lower.includes('aqa')) return false;
    if (lower.includes('gecko')) return false;
    if (lower.includes('classifier')) return false;
    if (lower.includes('discuss')) return false;
    if (lower.includes('imagen') && !lower.includes('gemini')) return false;
    return true;
  });
}

async function readListError(res: Response): Promise<string> {
  try {
    const j = (await res.json()) as { error?: { message?: string }; message?: string };
    return (j?.error?.message ?? j?.message ?? res.statusText) || `HTTP ${res.status}`;
  } catch {
    return res.statusText || `HTTP ${res.status}`;
  }
}

/**
 * Returns short model ids, e.g. "gemini-2.5-flash", sorted alphabetically.
 */
export async function fetchGeminiModelsForGenerateContent(apiKey: string): Promise<GeminiListModelsResult> {
  const key = apiKey.trim();
  if (!key) {
    return { ok: false, error: 'empty' };
  }

  const seen = new Set<string>();
  let pageToken: string | undefined;

  try {
    do {
      const params = new URLSearchParams({
        key,
        pageSize: '100',
      });
      if (pageToken) {
        params.set('pageToken', pageToken);
      }

      const url = `https://generativelanguage.googleapis.com/v1beta/models?${params.toString()}`;
      const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });

      if (!res.ok) {
        return { ok: false, error: await readListError(res) };
      }

      const data = (await res.json()) as {
        models?: Array<{
          name?: string;
          supportedGenerationMethods?: string[];
        }>;
        nextPageToken?: string;
      };

      for (const m of data.models ?? []) {
        const methods = m.supportedGenerationMethods ?? [];
        if (!methods.includes('generateContent')) continue;
        const full = m.name ?? '';
        const short = full.replace(/^models\//, '').trim();
        if (short) seen.add(short);
      }

      pageToken = data.nextPageToken;
    } while (pageToken);

    const rawIds = [...seen];
    const filtered = filterGeminiModelsForChat(rawIds);
    const finalIds = filtered.length > 0 ? filtered : rawIds;
    const modelIds = finalIds.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return { ok: true, modelIds };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, error: msg };
  }
}
